import React from "react";
import classNames from "classnames";
import { Link, NavLink } from "react-router-dom";

import { Container } from "../Grid/Grid";
import { Svg, SVG_ICONS } from "../__common/Svg/Svg";
import { Typography } from "../__common/Typography/Typography";
import { Tooltip } from "../__common/Tooltip/Tooltip";
import { HeaderMenu } from "../../constants/nav";

import "./Drawer.scss";
import { useEffect, useState } from "react";
import { Image } from "../__common/Image/Image";
import { LinkButton } from "../__common/_controls";
import { useUserHooks } from "../../hooks/user-hooks";
import { API_ENDPOINTS, API_SA_BASE_URL } from "../../constants/api";
import { LOGIN_ID_TYPE } from "../../types/user";

import { useTranslation } from "react-i18next";

import triggerGAEvent from "../../utils/UtilsTriggerGAEvent";
import { EVENT_CATEGORY_HEADER } from "../../constants/analytics";

interface Props {
  isDrawerOpen: boolean;
  isLogin?: boolean;
  onClickDrawerClose?: () => void;
  onClickLogin?: () => void;
  onClickSignOut?: () => void;
}

export const Drawer: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { userData } = useUserHooks();

  const {
    isDrawerOpen,
    onClickDrawerClose,
    isLogin,
    onClickLogin,
    onClickSignOut,
  } = props;
  const [activeTooltip, setActiveTooltip] = useState<string | number>("");

  const wrapClass = classNames("menu-drawer", { open: !!isDrawerOpen });

  const handleOnClickDrawerClose = () => {
    if (onClickDrawerClose) {
      handleOnClickTooltipClose();
      onClickDrawerClose();
    }
  };

  const handleMenuClick = (gaAction: string) => {
    if (onClickDrawerClose) {
      triggerGAEvent(EVENT_CATEGORY_HEADER, gaAction);
      onClickDrawerClose();
    }
  };

  const handleOnClickTooltip = (id: string | number) => {
    setActiveTooltip(id);
  };

  const handleOnClickTooltipClose = () => {
    setActiveTooltip("");
  };

  useEffect(() => {
    const body = document.body;
    if (isDrawerOpen) {
      body.classList.add("modal-open");
    } else {
      body.classList.remove("modal-open");
    }
  }, [isDrawerOpen]);

  return (
    <div className={wrapClass}>
      <div className="menu-drawer__close">
        <div
          className="menu-drawer__close__btn"
          onClick={handleOnClickDrawerClose}
        >
          <Svg icon={SVG_ICONS.DRAWER_CLOSE} width={24} height={24} />
        </div>
      </div>
      <Container>
        <div className="menu-drawer__profile">
          <div className="menu-drawer__profile__icon">
            {isLogin ? (
              userData.profileImageUrl ? (
                <Image
                  image={userData.profileImageUrl}
                  name={userData.name}
                  width={48}
                  height={48}
                  onClick={onClickLogin}
                />
              ) : (
                <Svg
                  icon={SVG_ICONS.PROFILE}
                  className="profile-placeholder"
                  width={48}
                  height={48}
                />
              )
            ) : (
              <Svg
                icon={SVG_ICONS.PROFILE_SIGNOUT_CIRCLE}
                width={48}
                height={48}
              />
            )}
          </div>
          <div className="menu-drawer__profile__text__container">
            <span onClick={onClickLogin}>
              <Typography
                className="menu-drawer__profile__text"
                variant="title3"
              >
                {isLogin ? userData.name : t("PRIVACY_HEADER_SIGNIN")}
              </Typography>
            </span>
          </div>
          {isLogin && (
            <div onClick={onClickSignOut}>
              <span className="menu-drawer__signout-button">
                {t("PRIVACY_HEADER_SIGNOUT")}
              </span>
            </div>
          )}
        </div>
        <div
          className={classNames("menu-drawer__message_container", {
            login: !!isLogin,
          })}
        >
          <Typography className="menu-drawer__message" variant="body1">
            {t("PRIVACY_HEADER_USER_POPUP_GREETING")} <br />
            {t("PRIVACY_HEADER_USER_POPUP_GREETING_TEXT")}
          </Typography>
        </div>
        {isLogin && (
          <div className="menu-drawer__email">
            <LinkButton
              title={
                userData.loginIdType === LOGIN_ID_TYPE.EMAIL
                  ? userData.validatedEmail
                  : userData.loginId
              }
              to={{
                pathname: `${API_SA_BASE_URL}${API_ENDPOINTS.SA_PROFILE}`,
              }}
              target="_blank"
              underlined={false}
              icon={SVG_ICONS.GREATE_THAN_SML}
              textClassName="menu-drawer__email__text"
            />
          </div>
        )}
        <div className="horizontal-divider" />
        <div className="menu-drawer__navs">
          <ul>
            {HeaderMenu.map((menu) => (
              <li className="menu-drawer__navs__item" key={menu.id}>
                <div className="nav-item">
                  <div className="item-icon">
                    {menu.icon && (
                      <Svg icon={menu.icon} width={22.9} height={18.4} />
                    )}
                  </div>
                  <div className="item-text">
                    {menu.title.link !== "" ? (
                      <Link
                        to={menu.title.link}
                        onClick={() => handleMenuClick(menu.gaAction)}
                      >
                        {t(menu.title.text)}
                      </Link>
                    ) : (
                      <Typography variant="title2">
                        {t(menu.title.text)}
                      </Typography>
                    )}
                  </div>
                  {menu.items && (
                    <div
                      className="item-tooltip"
                      onClick={() => handleOnClickTooltip(menu.id)}
                    >
                      <Svg
                        icon={SVG_ICONS.TOOLTIP_OUTLINE}
                        width={24}
                        height={24}
                      />
                    </div>
                  )}
                </div>
                {activeTooltip === menu.id && menu.items && (
                  <Tooltip
                    className="menu-item-tooltip"
                    onClickClose={handleOnClickTooltipClose}
                  >
                    {menu.items.map((item) => (
                      <>
                        <Typography className="tooltip-title" variant="body3">
                          {t(item.text)}
                        </Typography>
                        <Typography
                          className="tooltip-sub-title"
                          variant="body3"
                        >
                          {item.detail ? t(item.detail.text) : ""}
                        </Typography>
                      </>
                    ))}
                  </Tooltip>
                )}
                <ul className="sub-items">
                  {menu.items &&
                    menu.items.map((item) => (
                      <li className="sub-item-link" key={item.id}>
                        <NavLink
                          to={item.link}
                          onClick={() => handleMenuClick(item.gaAction)}
                        >
                          {t(item.text)}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </div>
  );
};
